// src/phone/index.ts
import {
  parsePhoneNumberWithError,
  isValidPhoneNumber,
  getCountries,
  getCountryCallingCode
} from "libphonenumber-js";
function validateMobilePhone(callingCode, localNumber) {
  if (!callingCode || !localNumber) return false;
  try {
    if (!isValidPhoneNumber(`${callingCode}${localNumber}`)) return false;
    return true;
  } catch {
    return false;
  }
}
function parsePhoneNumberInfo(phoneNumberString, defaultCountry) {
  try {
    if (!phoneNumberString || phoneNumberString.trim().length === 0) {
      return null;
    }
    const phoneNumber = parsePhoneNumberWithError(
      phoneNumberString,
      defaultCountry
    );
    if (!phoneNumber) {
      return null;
    }
    return {
      countryCode: `+${phoneNumber.countryCallingCode}`,
      phoneNumber: phoneNumber.nationalNumber,
      isValid: phoneNumber.isValid(),
      formattedNumber: phoneNumber.formatInternational()
    };
  } catch (error) {
    if (error instanceof Error) {
      console.warn(
        "Invalid phone number format:",
        phoneNumberString,
        error.message
      );
    } else {
      console.error("Error parsing phone number:", error);
    }
    return null;
  }
}
function getCallingCode(countryCode) {
  try {
    const callingCode = getCountryCallingCode(countryCode);
    return `+${callingCode}`;
  } catch (error) {
    console.error("Error getting calling code:", error);
    return null;
  }
}
function getAllCountryCallingCodes() {
  try {
    const countries = getCountries();
    return countries.map((countryCode) => ({
      countryCode,
      callingCode: `+${getCountryCallingCode(countryCode)}`
    }));
  } catch (error) {
    console.error("Error getting all country calling codes:", error);
    return [];
  }
}
function getCountryOptions() {
  try {
    const countries = getCountries();
    const seen = /* @__PURE__ */ new Set();
    return countries.map((countryCode) => {
      const callingCode = `+${getCountryCallingCode(countryCode)}`;
      return {
        value: callingCode,
        label: callingCode,
        supportingText: countryCode
      };
    }).filter((option) => {
      if (option.value === "+972") {
        return false;
      }
      if (seen.has(option.value)) {
        return false;
      }
      seen.add(option.value);
      return true;
    }).sort((a, b) => {
      const numA = parseInt(a.value.substring(1), 10);
      const numB = parseInt(b.value.substring(1), 10);
      return numA - numB;
    });
  } catch (error) {
    console.error("Error getting country options:", error);
    return [];
  }
}

// src/date/index.ts
var formatDate = (dateString) => {
  if (!dateString || dateString.trim() === "") return "-";
  const isoDateRegex = /^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}:\d{2}(\.\d{3,6})?Z?([+-]\d{2}:\d{2})?)?$/;
  if (!isoDateRegex.test(dateString)) return "-";
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return "-";
  const day = String(date.getUTCDate()).padStart(2, "0");
  const month = String(date.getUTCMonth() + 1).padStart(2, "0");
  const year = date.getUTCFullYear();
  return `${day}/${month}/${year}`;
};
var getDateKey = (dateString) => {
  const date = new Date(dateString);
  return date.toISOString().split("T")[0];
};
var getTimeKey = (dateString) => {
  const date = new Date(dateString);
  const hours = date.getHours().toString().padStart(2, "0");
  const minutes = date.getMinutes().toString().padStart(2, "0");
  return `${hours}:${minutes}`;
};

// src/countries/constants.ts
var GCC_COUNTRY_CODES = ["SA", "AE", "KW", "QA", "BH", "OM"];
var COUNTRIES_ENDPOINT = "/client-data/countries";
var COUNTRIES_QUERY_KEY = "countries";
var GCC_COUNTRIES_QUERY_KEY = "gcc-countries";

// src/countries/utils.ts
var localiseCountryName = (code, locale) => {
  try {
    const names = new Intl.DisplayNames([locale], { type: "region" });
    return names.of(code) ?? code;
  } catch {
    return code;
  }
};
var localiseAndSortCountries = (countries, locale) => {
  return countries.map((c) => ({ ...c, label: localiseCountryName(c.value, locale) })).sort((a, b) => a.label.localeCompare(b.label, locale));
};
var isValidCountry = (country) => {
  return country !== null && country !== void 0 && typeof country.code === "string" && typeof country.name === "string" && country.code.trim() !== "" && country.name.trim() !== "";
};
var transformToCountryOption = (country, includeSupportingText = false) => ({
  value: country.code,
  label: country.name,
  ...includeSupportingText && { supportingText: country.code }
});
var filterGCCCountries = (countries) => {
  return countries.filter(
    (country) => GCC_COUNTRY_CODES.includes(country.value)
  );
};
var filterInternationalCountries = (countries) => {
  return countries.filter((country) => country.value !== "SA");
};
var getCountriesByCompanyType = (isInternational, allCountries, gccCountries) => {
  if (isInternational) {
    return filterInternationalCountries(allCountries);
  }
  return gccCountries;
};
var validateAndTransformCountries = (countries, includeSupportingText = false) => {
  if (countries === null || countries === void 0) {
    return [];
  }
  if (!Array.isArray(countries)) {
    return [];
  }
  if (countries.length === 0) {
    return [];
  }
  const validCountries = countries.filter(isValidCountry);
  if (validCountries.length !== countries.length) {
  }
  return validCountries.map(
    (country) => transformToCountryOption(country, includeSupportingText)
  );
};
var getDisplayCountryValue = (code, allCountriesArr) => {
  if (!code) return "";
  if (!Array.isArray(allCountriesArr) || !allCountriesArr.length) return code;
  const found = allCountriesArr.find((c) => c.code === code);
  return found ? found.name : code;
};

// src/countries/hooks.ts
import { useMemo } from "react";
import { useApiQuery } from "@tasama/api-client-query";
import { useTranslation } from "@tasama/i18n";
var useCountries = (includeSupportingText = false) => {
  return useApiQuery(COUNTRIES_QUERY_KEY, COUNTRIES_ENDPOINT, {
    select: (data) => {
      if (!data) {
        console.error("No data received from countries API");
        return [];
      }
      const countriesData = data;
      return validateAndTransformCountries(
        countriesData.countries,
        includeSupportingText
      );
    },
    axiosConfig: {
      withCredentials: true
    }
  });
};
var useGCCCountries = (includeSupportingText = false) => {
  return useApiQuery(
    GCC_COUNTRIES_QUERY_KEY,
    COUNTRIES_ENDPOINT,
    {
      select: (data) => {
        if (!data) {
          console.error("No data received from countries API");
          return [];
        }
        const countriesData = data;
        const allCountries = validateAndTransformCountries(
          countriesData.countries,
          includeSupportingText
        );
        return filterGCCCountries(allCountries);
      },
      axiosConfig: {
        withCredentials: true
      }
    }
  );
};
var useLocalisedCountries = (includeSupportingText = false) => {
  const { i18n } = useTranslation("common");
  const result = useCountries(includeSupportingText);
  const localised = useMemo(
    () => result.data ? localiseAndSortCountries(result.data, i18n.language) : result.data,
    [result.data, i18n.language]
  );
  return { ...result, data: localised };
};
var useLocalisedGCCCountries = (includeSupportingText = false) => {
  const { i18n } = useTranslation("common");
  const result = useGCCCountries(includeSupportingText);
  const localised = useMemo(
    () => result.data ? localiseAndSortCountries(result.data, i18n.language) : result.data,
    [result.data, i18n.language]
  );
  return { ...result, data: localised };
};

// src/validations/email-validation.ts
import { z } from "zod";
var DISALLOWED_EMAIL_DOMAINS = [
  "gmail.com",
  "outlook.com",
  "yahoo.com",
  "hotmail.com",
  "aol.com",
  "live.com",
  "icloud.com",
  "me.com",
  "mac.com",
  "msn.com",
  "ymail.com",
  "rocketmail.com",
  "protonmail.com",
  "mail.com",
  "zoho.com",
  "gmx.com",
  "inbox.com",
  "fastmail.com",
  "mailinator.com"
];
var isDisallowedEmailDomain = (domain) => {
  const disallowedDomainsSet = new Set(
    DISALLOWED_EMAIL_DOMAINS
  );
  return disallowedDomainsSet.has(domain.toLowerCase());
};
var extractEmailDomain = (email) => {
  if (!email || typeof email !== "string") return "";
  const atCount = (email.match(/@/g) || []).length;
  if (atCount !== 1) return "";
  const lastAtIndex = email.lastIndexOf("@");
  if (lastAtIndex === -1 || lastAtIndex === 0 || lastAtIndex === email.length - 1)
    return "";
  const domain = email.substring(lastAtIndex + 1).toLowerCase();
  if (!domain || !/^[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(domain)) {
    return "";
  }
  return domain;
};
var isCorporateEmail = (email) => {
  if (!email || typeof email !== "string") return false;
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(email)) return false;
  const domain = extractEmailDomain(email);
  return domain !== "" && !isDisallowedEmailDomain(domain);
};
var createCorporateEmailSchema = (messages) => z.string().min(1, messages?.required || "Email is required").email(messages?.invalid || "Please enter a valid email address").superRefine((email, ctx) => {
  if (!email) return;
  if (!isCorporateEmail(email)) {
    ctx.addIssue({
      code: "custom",
      message: messages?.corporateRequired || "Please use your corporate email address. Personal email domains are not allowed."
    });
  }
});
var createEmailSchema = (options) => {
  const { required = true, corporate = false, messages = {} } = options || {};
  let schema = z.string();
  if (required) {
    schema = schema.min(1, messages.required || "Email is required");
  }
  schema = schema.email(
    messages.invalid || "Please enter a valid email address"
  );
  if (corporate) {
    schema = schema.superRefine((email, ctx) => {
      if (!email && !required) return;
      if (!email && required) return;
      if (!isCorporateEmail(email)) {
        ctx.addIssue({
          code: "custom",
          message: messages.corporate || "Please use your corporate email address. Personal email domains are not allowed."
        });
      }
    });
  }
  if (!required) {
    return schema.optional().or(z.literal(""));
  }
  return schema;
};
var corporateEmailSchema = createCorporateEmailSchema();
var optionalEmailSchema = createEmailSchema({
  required: false,
  messages: {
    invalid: "Please enter a valid email address"
  }
});
var optionalCorporateEmailSchema = createEmailSchema({
  required: false,
  corporate: true,
  messages: {
    invalid: "Please enter a valid email address",
    corporate: "Please use your corporate email address. Personal email domains are not allowed."
  }
});
var PIF_ALLOWED_DOMAINS = [
  "projects.pif.gov.sa",
  "publicissapient.com",
  "publicisgroupe.net"
];
var isPifEmail = (email) => {
  const domain = extractEmailDomain(email);
  return PIF_ALLOWED_DOMAINS.includes(domain);
};
var createConditionalEmailSchema = (options) => {
  const {
    required = true,
    companyType,
    companyStatus,
    messages = {}
  } = options || {};
  const isPifContext = companyType === "pif-owned" && companyStatus === "PIF_UNDER_DEVELOPMENT";
  let schema = z.string();
  if (required) {
    schema = schema.min(1, messages.required || "Email is required");
  }
  schema = schema.email(
    messages.invalid || "Please enter a valid email address"
  );
  schema = schema.superRefine((email, ctx) => {
    if (!email && !required) return;
    if (!email && required) return;
    if (isPifContext) {
      if (!isPifEmail(email)) {
        ctx.addIssue({
          code: "custom",
          message: messages.pif || "Please enter a valid PIF email address"
        });
      }
    } else {
      if (!isCorporateEmail(email)) {
        ctx.addIssue({
          code: "custom",
          message: messages.corporate || "Please use your corporate email address. Personal email domains are not allowed."
        });
      }
    }
  });
  if (!required) {
    return schema.optional().or(z.literal(""));
  }
  return schema;
};
var emailSchema = createEmailSchema();
var validateEmailWithCompanyContext = (email, companyType, companyStatus, messages) => {
  const schemaOptions = {
    required: true,
    ...companyType !== void 0 && { companyType },
    ...companyStatus !== void 0 && { companyStatus },
    ...messages !== void 0 && { messages }
  };
  const schema = createConditionalEmailSchema(schemaOptions);
  const result = schema.safeParse(email);
  if (!result.success) {
    return {
      isValid: false,
      error: result.error.issues[0]?.message || messages?.invalid || "Please enter a valid email address"
    };
  }
  return { isValid: true };
};

// src/validations/form-validation.ts
import { z as z2 } from "zod";
var ARABIC_LATIN_NAME_REGEX = /^[؀-ۿݐ-ݿA-Za-z\s-]*$/;
var ARABIC_LATIN_COMPANY_NAME_REGEX = /^[؀-ۿݐ-ݿA-Za-z0-9 &'.,()/-]*$/;
var ARABIC_LATIN_CITY_REGEX = /^[؀-ۿݐ-ݿA-Za-z\s-]*$/;
var createValidationSchema = {
  email: (messages) => {
    if (messages?.required) {
      return z2.string().min(1, messages.required).email(messages?.invalid || "Invalid email format");
    } else {
      return z2.string().email(messages?.invalid || "Invalid email format").optional().or(z2.literal(""));
    }
  },
  password: (minLength = 8, messages) => {
    if (messages?.required) {
      return z2.string().min(1, messages.required).min(
        minLength,
        messages?.minLength || `Password must be at least ${minLength} characters`
      );
    } else {
      return z2.string().min(
        minLength,
        messages?.minLength || `Password must be at least ${minLength} characters`
      ).optional();
    }
  },
  phone: (pattern = /^[+]?[1-9]\d{1,14}$/, messages) => {
    const phoneSchema = z2.string().regex(pattern, messages?.invalid || "Invalid phone number format");
    return messages?.required ? z2.string().min(1, messages.required).and(phoneSchema) : phoneSchema.optional();
  },
  url: (messages) => {
    const urlSchema = z2.string().url(messages?.invalid || "Invalid URL format");
    return messages?.required ? z2.string().min(1, messages.required).and(urlSchema) : urlSchema.optional();
  },
  number: (pattern = /^\d+$/, messages) => {
    const numberSchema = z2.string().regex(pattern, messages?.invalid || "Invalid number format");
    return messages?.required ? z2.string().min(1, messages.required).and(numberSchema) : numberSchema.optional();
  },
  required: (message = "This field is required") => z2.string().min(1, message),
  optional: () => z2.string().optional()
};
var createFieldValidation = (config) => {
  if (config.validation) {
    return config.validation;
  }
  switch (config.type) {
    case "email":
      return config.required ? z2.string().min(1, config.messages?.required || `${config.label} is required`).email(config.messages?.invalid || "Invalid email format") : z2.union([
        z2.string().email(config.messages?.invalid || "Invalid email format"),
        z2.literal("")
      ]).optional();
    case "password":
      return config.required ? z2.string().min(1, config.messages?.required || `${config.label} is required`).min(8, config.messages?.invalid || "Password too short") : z2.string().min(8, config.messages?.invalid || "Password too short").optional();
    case "tel":
      return config.required ? z2.string().min(1, config.messages?.required || `${config.label} is required`).regex(
        /^[+]?[1-9]\d{1,14}$/,
        config.messages?.invalid || "Invalid phone number"
      ) : z2.union([
        z2.string().regex(
          /^[+]?[1-9]\d{1,14}$/,
          config.messages?.invalid || "Invalid phone number"
        ),
        z2.literal("")
      ]).optional();
    case "url":
      return config.required ? z2.string().min(1, config.messages?.required || `${config.label} is required`).url(config.messages?.invalid || "Invalid URL") : z2.union([
        z2.string().url(config.messages?.invalid || "Invalid URL"),
        z2.literal("")
      ]).optional();
    case "number":
      return config.required ? z2.string().min(1, config.messages?.required || `${config.label} is required`).regex(/^\d+$/, config.messages?.invalid || "Invalid number") : z2.union([
        z2.string().regex(/^\d*$/, config.messages?.invalid || "Invalid number"),
        z2.literal("")
      ]).optional();
    default:
      return config.required ? z2.string().min(1, config.messages?.required || `${config.label} is required`) : z2.union([z2.string(), z2.literal("")]).optional();
  }
};
var createFormSchema = (config) => {
  if (config.schema) {
    return config.schema;
  }
  const schemaObject = {};
  config.fields.forEach((field) => {
    schemaObject[field.name] = createFieldValidation(field);
  });
  return z2.object(schemaObject);
};
var composeSchemas = (schemas) => {
  return z2.object(schemas);
};
var createFormValidator = (schema) => {
  const validate = (data) => {
    try {
      const result = schema.parse(data);
      return { success: true, data: result };
    } catch (error) {
      if (error instanceof z2.ZodError) {
        const errors = {};
        error.issues.forEach((issue) => {
          if (issue.path.length > 0) {
            errors[issue.path.join(".")] = issue.message;
          }
        });
        return { success: false, errors };
      }
      return { success: false, errors: { general: "Validation failed" } };
    }
  };
  const validateField = (fieldName, value) => {
    try {
      const fieldSchema = schema.shape?.[fieldName];
      if (fieldSchema) {
        fieldSchema.parse(value);
        return null;
      }
      return null;
    } catch (error) {
      if (error instanceof z2.ZodError) {
        return error.issues[0]?.message || "Invalid value";
      }
      return "Validation failed";
    }
  };
  return { validate, validateField };
};
var mergeSchemas = (...schemas) => {
  return schemas.reduce((acc, schema) => {
    if (acc instanceof z2.ZodObject && schema instanceof z2.ZodObject) {
      return acc.merge(schema);
    }
    return schema;
  });
};
var createConditionalValidation = (condition, trueSchema, falseSchema) => {
  return z2.any().superRefine((data, ctx) => {
    const schema = condition(data) ? trueSchema : falseSchema;
    const result = schema.safeParse(data);
    if (!result.success) {
      result.error.issues.forEach((issue) => {
        ctx.addIssue({
          code: "custom",
          path: issue.path,
          message: issue.message
        });
      });
    }
  });
};

// src/validations/password-validation.ts
import { z as z3 } from "zod";
var PASSWORD_MIN_LENGTH = 8;
var PASSWORD_MAX_LENGTH = 128;
var PASSWORD_PATTERNS = {
  // At least one lowercase letter
  LOWERCASE: /[a-z]/,
  // At least one uppercase letter
  UPPERCASE: /[A-Z]/,
  // At least one digit
  DIGIT: /[0-9]/,
  // At least one special character
  SPECIAL_CHAR: /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/
};
var checkPasswordStrength = (password) => {
  return {
    hasMinLength: password.length >= PASSWORD_MIN_LENGTH,
    hasMaxLength: password.length <= PASSWORD_MAX_LENGTH,
    hasLowercase: PASSWORD_PATTERNS.LOWERCASE.test(password),
    hasUppercase: PASSWORD_PATTERNS.UPPERCASE.test(password),
    hasDigit: PASSWORD_PATTERNS.DIGIT.test(password),
    hasSpecialChar: PASSWORD_PATTERNS.SPECIAL_CHAR.test(password)
  };
};
var getPasswordStrengthScore = (password) => {
  const strength = checkPasswordStrength(password);
  let score = 0;
  if (strength.hasMinLength) score++;
  if (strength.hasLowercase) score++;
  if (strength.hasUppercase) score++;
  if (strength.hasDigit) score++;
  if (strength.hasSpecialChar) score++;
  return score;
};
var getPasswordStrengthLevel = (password) => {
  const score = getPasswordStrengthScore(password);
  if (score <= 2) return "weak";
  if (score === 3) return "fair";
  if (score === 4) return "good";
  return "strong";
};
var createStrongPasswordSchema = (messages) => z3.string().min(1, messages?.required || "Password is required").min(
  PASSWORD_MIN_LENGTH,
  messages?.minLength || `Password must be at least ${PASSWORD_MIN_LENGTH} characters long`
).max(
  PASSWORD_MAX_LENGTH,
  messages?.maxLength || `Password must not exceed ${PASSWORD_MAX_LENGTH} characters`
).superRefine((password, ctx) => {
  if (!PASSWORD_PATTERNS.LOWERCASE.test(password)) {
    ctx.addIssue({
      code: "custom",
      message: messages?.lowercase || "Password must contain at least one lowercase letter"
    });
  }
  if (!PASSWORD_PATTERNS.UPPERCASE.test(password)) {
    ctx.addIssue({
      code: "custom",
      message: messages?.uppercase || "Password must contain at least one uppercase letter"
    });
  }
  if (!PASSWORD_PATTERNS.DIGIT.test(password)) {
    ctx.addIssue({
      code: "custom",
      message: messages?.digit || "Password must contain at least one number"
    });
  }
  if (!PASSWORD_PATTERNS.SPECIAL_CHAR.test(password)) {
    ctx.addIssue({
      code: "custom",
      message: messages?.specialChar || "Password must contain at least one special character"
    });
  }
});
var strongPasswordSchema = createStrongPasswordSchema();
var createPasswordConfirmationSchema = (passwordField = "password", messages) => {
  return z3.object({
    [passwordField]: createPasswordSchema({
      required: true,
      minLength: PASSWORD_MIN_LENGTH,
      maxLength: PASSWORD_MAX_LENGTH,
      requireUppercase: false,
      requireLowercase: false,
      requireDigit: false,
      requireSpecialChar: false
    }),
    confirmPassword: z3.string().min(1, messages?.confirmRequired || "Please confirm your password")
  }).superRefine((data, ctx) => {
    if (data[passwordField] !== data.confirmPassword) {
      ctx.addIssue({
        code: "custom",
        message: messages?.passwordsDoNotMatch || "Passwords do not match",
        path: ["confirmPassword"]
      });
    }
  });
};
var createStrongPasswordConfirmationSchema = (passwordField = "password", messages) => {
  return z3.object({
    [passwordField]: createStrongPasswordSchema(),
    confirmPassword: z3.string().min(1, messages?.confirmRequired || "Please confirm your password")
  }).superRefine((data, ctx) => {
    if (data[passwordField] !== data.confirmPassword) {
      ctx.addIssue({
        code: "custom",
        message: messages?.passwordsDoNotMatch || "Passwords do not match",
        path: ["confirmPassword"]
      });
    }
  });
};
var createPasswordSchema = (options) => {
  const {
    required = true,
    minLength = PASSWORD_MIN_LENGTH,
    maxLength = PASSWORD_MAX_LENGTH,
    requireUppercase = false,
    requireLowercase = false,
    requireDigit = false,
    requireSpecialChar = false,
    messages = {}
  } = options || {};
  let schema = z3.string();
  if (required) {
    schema = schema.min(1, messages.required || "Password is required");
  }
  schema = schema.min(
    minLength,
    messages.minLength || `Password must be at least ${minLength} characters long`
  ).max(
    maxLength,
    messages.maxLength || `Password must not exceed ${maxLength} characters`
  );
  if (requireLowercase || requireUppercase || requireDigit || requireSpecialChar) {
    schema = schema.superRefine((password, ctx) => {
      if (requireLowercase && !PASSWORD_PATTERNS.LOWERCASE.test(password)) {
        ctx.addIssue({
          code: "custom",
          message: messages.lowercase || "Password must contain at least one lowercase letter"
        });
      }
      if (requireUppercase && !PASSWORD_PATTERNS.UPPERCASE.test(password)) {
        ctx.addIssue({
          code: "custom",
          message: messages.uppercase || "Password must contain at least one uppercase letter"
        });
      }
      if (requireDigit && !PASSWORD_PATTERNS.DIGIT.test(password)) {
        ctx.addIssue({
          code: "custom",
          message: messages.digit || "Password must contain at least one number"
        });
      }
      if (requireSpecialChar && !PASSWORD_PATTERNS.SPECIAL_CHAR.test(password)) {
        ctx.addIssue({
          code: "custom",
          message: messages.specialChar || "Password must contain at least one special character"
        });
      }
    });
  }
  if (!required) {
    return schema.optional().or(z3.literal(""));
  }
  return schema;
};
var passwordSchema = createPasswordSchema({
  required: true,
  minLength: PASSWORD_MIN_LENGTH,
  maxLength: PASSWORD_MAX_LENGTH,
  requireUppercase: false,
  requireLowercase: false,
  requireDigit: false,
  requireSpecialChar: false
});

// src/store/index.ts
import { create } from "zustand";
import {
  subscribeWithSelector,
  persist,
  createJSONStorage
} from "zustand/middleware";
var createBaseStore = (initialState, customActions) => {
  return create()(
    subscribeWithSelector(
      (set, get) => ({
        // Base state
        loading: false,
        error: null,
        ...initialState,
        // Base actions
        setLoading: (loading) => set({ loading }),
        setError: (error) => set({ error }),
        clearError: () => set({ error: null }),
        // Custom actions
        ...customActions?.(set, get)
      })
    )
  );
};
var createEnhancedStore = (_storeName, initialState, customActions, messages) => {
  const store = create()(
    subscribeWithSelector((set, get) => {
      const createAsyncActionForStore = (action) => {
        return async (...args) => {
          try {
            set({ loading: true, error: null });
            const result = await action(...args);
            set({ loading: false });
            return result;
          } catch (error) {
            const errorMessage = error instanceof Error ? error.message : messages?.genericError || "An error occurred";
            set({ loading: false, error: errorMessage });
            throw error;
          }
        };
      };
      return {
        // Base state
        loading: false,
        error: null,
        ...initialState,
        // Base actions
        setLoading: (loading) => set({ loading }),
        setError: (error) => set({ error }),
        clearError: () => set({ error: null }),
        // Custom actions with async support
        ...customActions?.(set, get, createAsyncActionForStore)
      };
    })
  );
  return store;
};
var createAsyncAction = (store, action, messages) => {
  return async (...args) => {
    const { setLoading, setError, clearError } = store.getState();
    try {
      setLoading(true);
      clearError();
      const result = await action(...args);
      return result;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : messages?.genericError || "An error occurred";
      setError(errorMessage);
      throw error;
    } finally {
      setLoading(false);
    }
  };
};
var createStoreSelector = (store, selector) => {
  return () => store(selector);
};
var createStoreSubscription = (store, selector, callback) => {
  return () => {
    const unsubscribe = store.subscribe(selector, callback);
    return unsubscribe;
  };
};
var isPageReload = () => {
  if (typeof performance === "undefined") return false;
  const entries = performance.getEntriesByType("navigation");
  if (entries.length > 0) {
    return entries[0].type === "reload";
  }
  return false;
};
var createPersistedStore = (name, initialState, customActions, options) => {
  const store = create()(
    persist(
      subscribeWithSelector((set, get) => {
        const createAsyncActionForStore = (action) => {
          return async (...args) => {
            try {
              set({ loading: true, error: null });
              const result = await action(...args);
              set({ loading: false });
              return result;
            } catch (error) {
              const errorMessage = error instanceof Error ? error.message : options?.messages?.genericError || "An error occurred";
              set({ loading: false, error: errorMessage });
              throw error;
            }
          };
        };
        return {
          // Base state
          loading: false,
          error: null,
          ...initialState,
          // Base actions
          setLoading: (loading) => set({ loading }),
          setError: (error) => set({ error }),
          clearError: () => set({ error: null }),
          // Custom actions with async support
          ...customActions?.(set, get, createAsyncActionForStore)
        };
      }),
      {
        name: `store-${name}`,
        ...options?.partialize && { partialize: options.partialize },
        version: options?.version || 1,
        ...options?.migrate && { migrate: options.migrate },
        ...options?.onRehydrateStorage && {
          onRehydrateStorage: options.onRehydrateStorage
        },
        storage: options?.storage || createJSONStorage(() => sessionStorage)
      }
    )
  );
  if (options?.clearOnRefresh?.length && isPageReload()) {
    const resets = {};
    for (const key of options.clearOnRefresh) {
      resets[key] = initialState[key];
    }
    store.setState(resets);
  }
  return store;
};
var createStoreReset = (store, initialState) => {
  return () => {
    store.setState({
      ...initialState,
      loading: false,
      error: null
    });
  };
};
var createComputed = (store, selector, dependencies) => {
  let cachedValue;
  let cachedDeps;
  return () => {
    const state = store.getState();
    const currentDeps = dependencies.map((dep) => dep(state));
    if (!cachedDeps || currentDeps.some((dep, index) => dep !== cachedDeps[index])) {
      cachedValue = selector(state);
      cachedDeps = currentDeps;
    }
    return cachedValue;
  };
};
var createLogger = (_name, enabled = typeof window !== "undefined" && window.location?.hostname === "localhost") => {
  return (config) => (set, get, api) => {
    if (!enabled) {
      return config(set, get, api);
    }
    const loggedSet = (args) => {
      set(args);
    };
    return config(loggedSet, get, api);
  };
};
var createDevtools = (name, enabled = typeof window !== "undefined" && window.location?.hostname === "localhost") => {
  return (config) => (set, get, api) => {
    if (!enabled || typeof window === "undefined" || !window.__REDUX_DEVTOOLS_EXTENSION__) {
      return config(set, get, api);
    }
    const devtools = window.__REDUX_DEVTOOLS_EXTENSION__.connect({ name });
    const devtoolsSet = (args) => {
      set(args);
      const newState = get();
      devtools.send(args, newState);
    };
    return config(devtoolsSet, get, api);
  };
};
var createValidator = (validator, errorMessage = "Invalid state") => {
  return (config) => (set, get, api) => {
    const validatedSet = (args) => {
      set(args);
      const newState = get();
      if (!validator(newState)) {
        throw new Error(errorMessage);
      }
    };
    return config(validatedSet, get, api);
  };
};

// src/string/index.ts
var DEFAULT_PREFIX_OPTIONS = [
  { value: "Mr", label: "Mr" },
  { value: "Ms", label: "Ms" },
  { value: "Mrs", label: "Mrs" },
  { value: "Miss", label: "Miss" },
  { value: "Dr", label: "Dr" }
];
function removeSalutation(name, options = {}) {
  if (typeof name !== "string") {
    throw new TypeError("Name must be a string");
  }
  if (!name.trim()) {
    return name;
  }
  const {
    customSalutations = [],
    useDefaults = true,
    caseSensitive = false,
    trimResult = true
  } = options;
  const salutationsToRemove = [];
  if (useDefaults) {
    DEFAULT_PREFIX_OPTIONS.forEach((option) => {
      salutationsToRemove.push(option.value, option.label);
    });
  }
  if (customSalutations.length > 0) {
    salutationsToRemove.push(...customSalutations);
  }
  if (salutationsToRemove.length === 0) {
    return trimResult ? name.trim() : name;
  }
  let processedName = name;
  for (const salutation of salutationsToRemove) {
    if (!salutation) continue;
    const salutationToCheck = caseSensitive ? salutation : salutation.toLowerCase();
    const nameToCheck = caseSensitive ? processedName : processedName.toLowerCase();
    const patterns = [
      `${salutationToCheck}. `,
      // "Mr. " (prioritize this pattern)
      `${salutationToCheck} `,
      // "Mr "
      `${salutationToCheck}.`
      // "Mr." (without space)
    ];
    for (const pattern of patterns) {
      if (nameToCheck.startsWith(pattern)) {
        const startIndex = pattern.length;
        processedName = processedName.substring(startIndex);
        return trimResult ? processedName.trim() : processedName;
      }
    }
  }
  return trimResult ? processedName.trim() : processedName;
}
function decodeHtmlEntities(str) {
  return str.replace(/&amp;/g, "&").replace(/&lt;/g, "<").replace(/&gt;/g, ">").replace(/&quot;/g, '"').replace(/&apos;/g, "'").replace(/&nbsp;/g, " ").replace(
    /&#x([0-9a-fA-F]+);/g,
    (_, hex) => String.fromCharCode(parseInt(hex, 16))
  ).replace(/&#(\d+);/g, (_, dec) => String.fromCharCode(Number(dec)));
}
function removeHtmlTag(html, tagName) {
  const escaped = tagName.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const tagRegex = new RegExp(`<\\/?${escaped}(?:\\s[^>]*)?>`, "gi");
  return decodeHtmlEntities(html.replace(tagRegex, ""));
}
function removeAllHtmlTags(html) {
  return decodeHtmlEntities(html.replace(/<[^>]*>/g, ""));
}
function toAsciiDigits(value) {
  return value.replace(/[٠-٩]/g, (d) => String(d.charCodeAt(0) - 1632)).replace(/[۰-۹]/g, (d) => String(d.charCodeAt(0) - 1776));
}

// src/url/index.ts
function isSafeUrl(url) {
  if (!url) return false;
  if (url.startsWith("//")) return false;
  if (url.startsWith("/")) return true;
  try {
    const parsed = new URL(url);
    return parsed.protocol === "http:" || parsed.protocol === "https:";
  } catch {
    return false;
  }
}

// src/file-upload.ts
import { z as z4 } from "zod";
function computeUploadTimeout(totalBytes, fileCount = 1) {
  const BYTES_PER_MS = 524;
  const BUFFER_MS = 3e4;
  const PER_FILE_MS = 5e3;
  const MIN_MS = 12e4;
  const MAX_MS = 6e5;
  return Math.min(
    MAX_MS,
    Math.max(
      MIN_MS,
      BUFFER_MS + PER_FILE_MS * Math.max(1, fileCount) + 2 * Math.ceil(totalBytes / BYTES_PER_MS)
    )
  );
}
function buildFileFormData(file, fields = {}) {
  const fd = new FormData();
  fd.append("file", file);
  Object.entries(fields).forEach(([key, value]) => fd.append(key, value));
  return fd;
}
var toBytes = (file) => {
  if (typeof file.bytes === "number" && Number.isFinite(file.bytes)) {
    return file.bytes;
  }
  const raw = typeof file.fileSize === "string" ? file.fileSize.trim() : "";
  if (!raw) return null;
  const m = raw.match(/^(\d+(?:\.\d+)?)\s*(kb|mb|gb|b|bytes)?$/i);
  if (!m) return null;
  const n = parseFloat(m[1] ?? "");
  const unit = (m[2] || "b").toLowerCase();
  if (Number.isNaN(n)) return null;
  switch (unit) {
    case "gb":
      return Math.round(n * 1024 * 1024 * 1024);
    case "mb":
      return Math.round(n * 1024 * 1024);
    case "kb":
      return Math.round(n * 1024);
    default:
      return Math.round(n);
  }
};
var uploadStatusSchema = z4.enum([
  "uploading",
  "complete",
  "error",
  "pending"
]);
var uploadedFileBaseSchema = z4.object({
  id: z4.string().min(1),
  fileName: z4.string().min(1),
  fileSize: z4.string().min(1),
  bytes: z4.number().int().min(0).optional(),
  progress: z4.number().min(0).max(100),
  status: uploadStatusSchema
});
function createUploadedFilesSchema(config = {}) {
  const {
    maxBytes = 5 * 1024 * 1024,
    allowedExts = ["pdf", "png", "jpg"],
    minFiles = 0,
    requireComplete = false,
    messages = {}
  } = config;
  const {
    required = messages.required || "Please upload at least one document",
    mustComplete = messages.mustComplete || "Please wait until the document finishes uploading",
    tooLarge = messages.tooLarge || `Maximum file size is ${Math.round(maxBytes / (1024 * 1024))} MB per document`,
    invalidSize = messages.invalidSize || "Invalid file size format",
    invalidType = messages.invalidType || `Unsupported file type. Allowed types: ${allowedExts.map((e) => e.toUpperCase()).join(", ")}`
  } = messages;
  const allowedExtsSet = new Set(allowedExts);
  const baseArray = z4.array(uploadedFileBaseSchema);
  const withMin = minFiles > 0 ? baseArray.min(minFiles, required) : baseArray;
  return withMin.default([]).superRefine((files, ctx) => {
    if (files.length === 0) return;
    if (requireComplete) {
      const hasCompleteFile = files.some((f) => f.status === "complete");
      if (!hasCompleteFile && files.some((f) => f.status === "uploading")) {
        ctx.addIssue({
          code: "custom",
          message: mustComplete,
          path: []
        });
      }
    }
    files.forEach((file) => {
      const ext = (file.fileName?.split(".").pop() || "").toLowerCase();
      if (!allowedExtsSet.has(ext)) {
        ctx.addIssue({
          code: "custom",
          message: invalidType,
          path: []
        });
      }
      const bytes = toBytes(file);
      if (bytes === null) {
        ctx.addIssue({
          code: "custom",
          message: invalidSize,
          path: []
        });
      } else if (bytes > maxBytes) {
        ctx.addIssue({
          code: "custom",
          message: tooLarge,
          path: []
        });
      }
    });
  });
}
function createFileUploadValidator(schema, options) {
  return (data) => {
    try {
      const result = schema.parse(data);
      return { success: true, data: result };
    } catch (error) {
      if (error instanceof z4.ZodError) {
        const errors = {};
        error.issues.forEach((issue) => {
          const key = issue.path[0]?.toString() ?? "uploadedFiles";
          if (!errors[key]) {
            errors[key] = issue.message;
          }
        });
        return { success: false, errors };
      }
      return {
        success: false,
        errors: {
          general: options?.genericError || "Validation failed"
        }
      };
    }
  };
}

// src/hooks/use-file-upload-progress.ts
import { useCallback, useEffect, useRef } from "react";
function useFileUploadProgress() {
  const intervalsRef = useRef(
    /* @__PURE__ */ new Map()
  );
  const timeoutsRef = useRef(
    /* @__PURE__ */ new Map()
  );
  const startProgress = useCallback((options) => {
    const {
      fileId,
      stepSize = 10,
      intervalMs = 200,
      delayMs = 0,
      getLatestProgress,
      onUpdate,
      onComplete
    } = options;
    const startInterval = () => {
      const interval = setInterval(() => {
        const currentProgress = getLatestProgress();
        if (currentProgress === void 0) {
          clearInterval(interval);
          intervalsRef.current.delete(fileId);
          return;
        }
        const newProgress = Math.min(currentProgress + stepSize, 100);
        const status = newProgress >= 100 ? "complete" : "uploading";
        onUpdate(newProgress, status);
        if (newProgress >= 100) {
          clearInterval(interval);
          intervalsRef.current.delete(fileId);
          onComplete?.();
        }
      }, intervalMs);
      intervalsRef.current.set(fileId, interval);
    };
    if (delayMs > 0) {
      const timeout = setTimeout(startInterval, delayMs);
      timeoutsRef.current.set(fileId, timeout);
    } else {
      startInterval();
    }
  }, []);
  const cancelProgress = useCallback((fileId) => {
    const interval = intervalsRef.current.get(fileId);
    if (interval) {
      clearInterval(interval);
      intervalsRef.current.delete(fileId);
    }
    const timeout = timeoutsRef.current.get(fileId);
    if (timeout) {
      clearTimeout(timeout);
      timeoutsRef.current.delete(fileId);
    }
  }, []);
  useEffect(() => {
    const intervals = intervalsRef.current;
    const timeouts = timeoutsRef.current;
    return () => {
      intervals.forEach((interval) => clearInterval(interval));
      timeouts.forEach((timeout) => clearTimeout(timeout));
    };
  }, []);
  return { startProgress, cancelProgress };
}

// src/analytics.ts
function trackPageView(path, title) {
  if (typeof window.gtag !== "function") return;
  window.gtag("event", "page_view", {
    page_path: path,
    page_title: title ?? document.title,
    page_location: window.location.origin + path
  });
}
function trackEvent(eventName, params) {
  if (typeof window.gtag !== "function") return;
  window.gtag("event", eventName, params);
}

// src/index.ts
import { z as z5 } from "zod";
export {
  ARABIC_LATIN_CITY_REGEX,
  ARABIC_LATIN_COMPANY_NAME_REGEX,
  ARABIC_LATIN_NAME_REGEX,
  COUNTRIES_ENDPOINT,
  COUNTRIES_QUERY_KEY,
  DEFAULT_PREFIX_OPTIONS,
  DISALLOWED_EMAIL_DOMAINS,
  GCC_COUNTRIES_QUERY_KEY,
  GCC_COUNTRY_CODES,
  PASSWORD_MAX_LENGTH,
  PASSWORD_MIN_LENGTH,
  PASSWORD_PATTERNS,
  PIF_ALLOWED_DOMAINS,
  buildFileFormData,
  checkPasswordStrength,
  composeSchemas,
  computeUploadTimeout,
  corporateEmailSchema,
  createAsyncAction,
  createBaseStore,
  createComputed,
  createConditionalEmailSchema,
  createConditionalValidation,
  createCorporateEmailSchema,
  createDevtools,
  createEmailSchema,
  createEnhancedStore,
  createFieldValidation,
  createFileUploadValidator,
  createFormSchema,
  createFormValidator,
  createLogger,
  createPasswordConfirmationSchema,
  createPasswordSchema,
  createPersistedStore,
  createStoreReset,
  createStoreSelector,
  createStoreSubscription,
  createStrongPasswordConfirmationSchema,
  createStrongPasswordSchema,
  createUploadedFilesSchema,
  createValidationSchema,
  createValidator,
  emailSchema,
  extractEmailDomain,
  filterGCCCountries,
  filterInternationalCountries,
  formatDate,
  getAllCountryCallingCodes,
  getCallingCode,
  getCountriesByCompanyType,
  getCountryOptions,
  getDateKey,
  getDisplayCountryValue,
  getPasswordStrengthLevel,
  getPasswordStrengthScore,
  getTimeKey,
  isCorporateEmail,
  isDisallowedEmailDomain,
  isPifEmail,
  isSafeUrl,
  isValidCountry,
  localiseAndSortCountries,
  localiseCountryName,
  mergeSchemas,
  optionalCorporateEmailSchema,
  optionalEmailSchema,
  parsePhoneNumberInfo,
  passwordSchema,
  removeAllHtmlTags,
  removeHtmlTag,
  removeSalutation,
  strongPasswordSchema,
  toAsciiDigits,
  toBytes,
  trackEvent,
  trackPageView,
  transformToCountryOption,
  uploadStatusSchema,
  uploadedFileBaseSchema,
  useCountries,
  useFileUploadProgress,
  useGCCCountries,
  useLocalisedCountries,
  useLocalisedGCCCountries,
  validateAndTransformCountries,
  validateEmailWithCompanyContext,
  validateMobilePhone,
  z5 as z
};
//# sourceMappingURL=index.js.map